/**
 * subject-tracker.js — shared subject detect + track engine for "smart crop /
 * auto-reframe". Extracted from the proven video-blur face-detect + NCC
 * auto-track path (measured & validated 2026-07-11, see memory
 * reference_smart_crop_reframe_feasibility_2026_07_11).
 *
 * Pipeline it powers: detect the subject (MediaPipe BlazeFace) → track it across
 * the clip with pure-JS NCC template matching (0 bytes, CSP-clean, scene-cut +
 * lost detection) → smooth the centre trajectory → clamp a fixed-AR crop window
 * inside the frame → hand an animated crop to the caller (WebCodecs per-frame
 * draw or an FFmpeg piecewise-linear crop expression).
 *
 * Framework-agnostic: the caller passes MediaPipe source URLs (the Blade `cdn()`
 * helper cannot run inside a static .js) and a seek() for frame access.
 *
 * Public API — window.SubjectTracker:
 *   ensureDetector(sources)                         → Promise<detector>
 *   detectFaces(source, srcW, srcH, {tiled})        → Promise<[{x,y,w,h} 0..1]>
 *   track({videoEl, seek, seedBox, startT, endT,
 *          interval, onProgress, signal})           → Promise<{keyframes,lost,lostReason}>
 *   buildCropPath({keyframes, cw, ch, vw, vh, tau}) → { at(t)→{x,y}, samples:[{t,x,y}] }
 *   ffmpegCropExpr(path, cw, ch, vw, vh, maxPts)    → { x:'<expr>', y:'<expr>' }
 *
 * `seedBox`, keyframes and detect boxes are all in NORMALISED 0..1 source coords.
 */
(function (root) {
    'use strict';

    // ── MediaPipe loaders (mirror video-blur ensureFaceDetector) ─────────────────
    // Sources tried as PAIRS (esm + wasm root + model), each validated by actually
    // constructing the task — forVisionTasks(deadUrl) resolves, so only the
    // create call is an honest check of the files. Self-hosted first, CDN as recovery.
    //
    // "Created" is not "works": a GPU graph comes up on the first inference, and a
    // crippled WebGL2 (no float textures) dies there, past this ladder. So a task
    // answers one real detect before it counts (timbrica-core.js, __mpSoftFit.probe),
    // and a delegate that failed it is dead for every source and both tasks — the
    // GPU graph is the device's, not the file's. On a software GL the door says GPU
    // is 3× slower (__mpSoftFit.gpuFirst): the caller's CPU sources go first there.
    var _dead = {};
    function ordered(sources) {
        var door = root.__mpSoftFit;
        if (!door || !door.gpuFirst || door.gpuFirst()) return sources.slice();
        var gpu = function (s) { return (s.delegate || 'GPU') === 'GPU'; };
        return sources.filter(function (s) { return !gpu(s); }).concat(sources.filter(gpu));
    }
    async function raise(Task, sources, makeOpts) {
        var lastErr = null, door = root.__mpSoftFit, list = ordered(sources);
        for (var i = 0; i < list.length; i++) {
            var src = list[i];
            try {
                var mp = await import(/* webpackIgnore: true */ src.esm);
                var vision = await mp.FilesetResolver.forVisionTasks(src.wasm);
                var opts = makeOpts(src);
                // Без WebGL2 — CPU и программный «WebGL2» (timbrica-core.js, __mpSoftFit).
                if (door) await door(opts);
                var dg = opts.baseOptions.delegate;
                if (_dead[dg]) continue;
                var task = await mp[Task].createFromOptions(vision, opts);
                if (door && door.probe && !door.probe(task, 'IMAGE')) { _dead[dg] = 1; lastErr = new Error('probe failed on ' + dg); continue; }
                return task;
            } catch (e) { lastErr = e; }
        }
        throw lastErr || new Error(Task + ' unavailable');
    }

    var _detector = null, _ready = null;
    function ensureDetector(sources) {
        if (_detector) return Promise.resolve(_detector);
        if (_ready) return _ready;
        _ready = raise('FaceDetector', sources, function (src) {
            return {
                baseOptions: { modelAssetPath: src.model, delegate: src.delegate || 'GPU' },
                runningMode: 'IMAGE', minDetectionConfidence: 0.5,
            };
        }).then(function (d) { _detector = d; return d; }, function (e) {
            _ready = null; // let a later call retry after a transient failure
            throw e;
        });
        return _ready;
    }

    // ── Tiled face detection (video-blur constants: 4×4 tiles = best recall) ────
    var FD_GRID = 4, FD_OVERLAP = 0.25, FD_UPSCALE = 2.5, FD_NMS = 0.3;
    function iou(a, b) {
        var x1 = Math.max(a.x, b.x), y1 = Math.max(a.y, b.y);
        var x2 = Math.min(a.x + a.w, b.x + b.w), y2 = Math.min(a.y + a.h, b.y + b.h);
        var inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
        return inter / (a.w * a.h + b.w * b.h - inter);
    }
    function nms(boxes) {
        var keep = [];
        boxes.sort(function (a, b) { return b.w * b.h - a.w * a.h; });
        for (var i = 0; i < boxes.length; i++) {
            var b = boxes[i], ok = true;
            for (var j = 0; j < keep.length; j++) { if (iou(keep[j], b) > FD_NMS) { ok = false; break; } }
            if (ok) keep.push(b);
        }
        return keep;
    }
    // Detect faces in `source` (video/canvas). Returns NORMALISED boxes. tiled=true
    // also sweeps overlapping tiles (slower, ~450ms@720p vs ~40ms) for small faces.
    async function detectFaces(source, srcW, srcH, opts) {
        if (!_detector) throw new Error('detector not ready');
        opts = opts || {};
        var out = [];
        var push = function (dets, ox, oy, s) {
            for (var i = 0; i < dets.length; i++) {
                var bb = dets[i].boundingBox;
                out.push({ x: (ox + bb.originX / s) / srcW, y: (oy + bb.originY / s) / srcH, w: (bb.width / s) / srcW, h: (bb.height / s) / srcH });
            }
        };
        // Full-frame coarse pass — catches large/close faces the tiles would clip.
        var k = Math.min(640 / Math.max(srcW, srcH), 1);
        var c = document.createElement('canvas');
        c.width = Math.round(srcW * k); c.height = Math.round(srcH * k);
        c.getContext('2d').drawImage(source, 0, 0, c.width, c.height);
        push(_detector.detect(c).detections || [], 0, 0, k);

        if (opts.tiled) {
            var step = 1 - FD_OVERLAP;
            var tw = srcW / (FD_GRID - (FD_GRID - 1) * FD_OVERLAP);
            var th = srcH / (FD_GRID - (FD_GRID - 1) * FD_OVERLAP);
            var s = Math.min(FD_UPSCALE, 640 / tw);
            var tc = document.createElement('canvas');
            tc.width = Math.round(tw * s); tc.height = Math.round(th * s);
            var tctx = tc.getContext('2d');
            for (var gy = 0; gy < FD_GRID; gy++) for (var gx = 0; gx < FD_GRID; gx++) {
                var sx = Math.min(gx * tw * step, srcW - tw), sy = Math.min(gy * th * step, srcH - th);
                tctx.clearRect(0, 0, tc.width, tc.height);
                tctx.drawImage(source, sx, sy, tw, th, 0, 0, tc.width, tc.height);
                push(_detector.detect(tc).detections || [], sx, sy, s);
            }
        }
        return nms(out);
    }

    // ── MediaPipe ObjectDetector (EfficientDet-Lite2) — person/object fallback ───
    // Seeds the tracker when no FACE is found (surfer, pet, car, product demo…).
    // COCO 80 classes; "person" is the usual subject, else the biggest confident box.
    var _objDetector = null, _objReady = null;
    function ensureObjectDetector(sources) {
        if (_objDetector) return Promise.resolve(_objDetector);
        if (_objReady) return _objReady;
        _objReady = raise('ObjectDetector', sources, function (src) {
            return {
                baseOptions: { modelAssetPath: src.model, delegate: src.delegate || 'GPU' },
                runningMode: 'IMAGE', scoreThreshold: src.scoreThreshold || 0.35, maxResults: 25,
            };
        }).then(function (d) { _objDetector = d; return d; }, function (e) {
            _objReady = null;
            throw e;
        });
        return _objReady;
    }
    // Full-frame detect at 640px. Returns NORMALISED boxes {x,y,w,h,cat,score}.
    async function detectObjects(source, srcW, srcH) {
        if (!_objDetector) throw new Error('object detector not ready');
        var k = Math.min(640 / Math.max(srcW, srcH), 1);
        var c = document.createElement('canvas');
        c.width = Math.round(srcW * k); c.height = Math.round(srcH * k);
        c.getContext('2d').drawImage(source, 0, 0, c.width, c.height);
        var res = _objDetector.detect(c), out = [];
        (res.detections || []).forEach(function (d) {
            var bb = d.boundingBox, cat = (d.categories && d.categories[0]) || {};
            out.push({ x: bb.originX / k / srcW, y: bb.originY / k / srcH, w: bb.width / k / srcW, h: bb.height / k / srcH, cat: cat.categoryName || '', score: cat.score || 0 });
        });
        return out;
    }
    // Pick the subject to follow: prefer "person", else the largest confident box.
    function pickSubject(objs) {
        if (!objs || !objs.length) return null;
        var persons = objs.filter(function (o) { return o.cat === 'person'; });
        var pool = persons.length ? persons : objs;
        var best = pool[0];
        for (var i = 1; i < pool.length; i++) if (pool[i].w * pool[i].h > best.w * best.h) best = pool[i];
        return best;
    }

    // ── NCC template matching (verbatim from video-blur autoTrackRegion) ────────
    function grayPatch(ctx, W, H, bx, by, bw, bh) {
        bx = clamp(Math.round(bx), 0, W - 1); by = clamp(Math.round(by), 0, H - 1);
        bw = clamp(Math.round(bw), 2, W - bx); bh = clamp(Math.round(bh), 2, H - by);
        var img = ctx.getImageData(bx, by, bw, bh).data, n = bw * bh, g = new Float32Array(n), sum = 0;
        for (var i = 0, p = 0; i < n; i++, p += 4) { var v = 0.299 * img[p] + 0.587 * img[p + 1] + 0.114 * img[p + 2]; g[i] = v; sum += v; }
        var mean = sum / n, ss = 0; for (var j = 0; j < n; j++) { var d = g[j] - mean; ss += d * d; }
        return { g: g, w: bw, h: bh, mean: mean, std: Math.sqrt(ss / n) || 1 };
    }
    function blendTemplate(t, nu, a) {
        if (nu.w !== t.w || nu.h !== t.h) return;
        var n = t.g.length, sum = 0;
        for (var i = 0; i < n; i++) { t.g[i] = t.g[i] * (1 - a) + nu.g[i] * a; sum += t.g[i]; }
        t.mean = sum / n; var ss = 0; for (var j = 0; j < n; j++) { var d = t.g[j] - t.mean; ss += d * d; } t.std = Math.sqrt(ss / n) || 1;
    }
    function nccAt(sg, sW, ox, oy, tmpl) {
        var tw = tmpl.w, th = tmpl.h, tg = tmpl.g, tm = tmpl.mean, tstd = tmpl.std, sum = 0;
        for (var y = 0; y < th; y++) { var row = (oy + y) * sW + ox; for (var x = 0; x < tw; x++) sum += sg[row + x]; }
        var pm = sum / (tw * th), cov = 0, ss = 0;
        for (var y2 = 0; y2 < th; y2++) { var row2 = (oy + y2) * sW + ox, trow = y2 * tw; for (var x2 = 0; x2 < tw; x2++) { var pv = sg[row2 + x2] - pm, tv = tg[trow + x2] - tm; cov += pv * tv; ss += pv * pv; } }
        return cov / (tw * th * (Math.sqrt(ss / (tw * th)) || 1) * tstd);
    }
    function nccSearch(ctx, W, H, tmpl, bx, by, bw, bh, gain) {
        var margin = Math.max(8, Math.round(Math.max(bw, bh) * (gain || 0.6)));
        var sx = clamp(Math.round(bx - margin), 0, W - 1), sy = clamp(Math.round(by - margin), 0, H - 1);
        var sw = clamp(Math.round(bw + margin * 2), tmpl.w, W - sx), sh = clamp(Math.round(bh + margin * 2), tmpl.h, H - sy);
        if (sw < tmpl.w || sh < tmpl.h) return null;
        var img = ctx.getImageData(sx, sy, sw, sh).data, g = new Float32Array(sw * sh);
        for (var i = 0, p = 0; i < g.length; i++, p += 4) g[i] = 0.299 * img[p] + 0.587 * img[p + 1] + 0.114 * img[p + 2];
        var best = -2, bestX = 0, bestY = 0;
        for (var oy = 0; oy + tmpl.h <= sh; oy += 2) for (var ox = 0; ox + tmpl.w <= sw; ox += 2) { var s = nccAt(g, sw, ox, oy, tmpl); if (s > best) { best = s; bestX = ox; bestY = oy; } }
        for (var oy2 = Math.max(0, bestY - 1); oy2 <= Math.min(sh - tmpl.h, bestY + 1); oy2++) for (var ox2 = Math.max(0, bestX - 1); ox2 <= Math.min(sw - tmpl.w, bestX + 1); ox2++) { var s2 = nccAt(g, sw, ox2, oy2, tmpl); if (s2 > best) { best = s2; bestX = ox2; bestY = oy2; } }
        return { x: sx + bestX, y: sy + bestY, score: best };
    }
    // Coarse subsampled frame signature → scene-cut detection (local NCC is blind
    // across a hard cut and would drift onto random background).
    function frameSig(ctx, W, H) {
        var step = Math.max(2, Math.round(Math.min(W, H) / 48));
        var cols = Math.floor(W / step), rows = Math.floor(H / step);
        if (cols < 1 || rows < 1) return null;
        var img = ctx.getImageData(0, 0, W, H).data, sig = new Float32Array(cols * rows), k = 0;
        for (var y = 0; y < rows; y++) { var yy = y * step; for (var x = 0; x < cols; x++) { var p = (yy * W + x * step) * 4; sig[k++] = 0.299 * img[p] + 0.587 * img[p + 1] + 0.114 * img[p + 2]; } }
        return sig;
    }
    function sigDiff(a, b) { if (!a || !b || a.length !== b.length) return 0; var s = 0; for (var i = 0; i < a.length; i++) s += Math.abs(a[i] - b[i]); return s / a.length; }
    function median(arr) { if (!arr.length) return 0; var s = arr.slice().sort(function (x, y) { return x - y; }), m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; }
    var CUT_ABS = 22, CUT_REL = 2.5, NCC_LOST = 0.30, NCC_LOST_MAX = 3;

    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
    function seeked(v) { return new Promise(function (res) { var h = function () { v.removeEventListener('seeked', h); res(); }; v.addEventListener('seeked', h); }); }

    // ── Track a NORMALISED seed box across [startT, endT] ───────────────────────
    // The template is grabbed AT THE SEED FRAME (opts.seedTime), then tracked
    // outward in BOTH directions — so the seed box POSITION and the template
    // CONTENT always come from the same frame (a mismatch systematically biases
    // the tracked centre). Returns time-ascending keyframes [{time,x,y,w,h} 0..1].
    async function track(opts) {
        var video = opts.videoEl, seek = opts.seek || function (t) { video.currentTime = t; return seeked(video); };
        var vw = video.videoWidth, vh = video.videoHeight;
        var startT = clamp(opts.startT || 0, 0, video.duration);
        var endT = Math.min(opts.endT != null ? opts.endT : video.duration, video.duration);
        var seedTime = clamp(opts.seedTime != null ? opts.seedTime : startT, startT, endT);
        // NCC search margin as a fraction of box size. 1.0 (not 0.6) measured
        // 2026-07-12: at 0.6 a fast subject jumps past the search window between
        // 0.3s samples and the track is LOST (RMSE 0.16); 1.0 recovers it (0.018)
        // with NO extra seeks — strictly better than denser sampling.
        var searchGain = opts.searchGain || 1.0;
        var seed = opts.seedBox; // normalised
        var boxFrac = Math.max(0.04, Math.max(seed.w, seed.h));
        var maxDim = clamp(Math.round(56 / boxFrac), 160, 360); // keep template ~56px → fast NCC
        var sc = Math.min(maxDim / vw, maxDim / vh, 1);
        var W = Math.max(2, Math.round(vw * sc)), H = Math.max(2, Math.round(vh * sc));
        var tc = document.createElement('canvas'); tc.width = W; tc.height = H;
        var tctx = tc.getContext('2d', { willReadFrequently: true });
        var interval = opts.interval || Math.max(video.duration > 120 ? 0.5 : 0.3, (endT - startT) / 200);
        var lostReason = null;

        // Seed template + box from the detection frame.
        var seedBW = Math.max(8, seed.w * W), seedBH = Math.max(8, seed.h * H);
        var seedBX = clamp(seed.x * W, 0, W - seedBW), seedBY = clamp(seed.y * H, 0, H - seedBH);
        await seek(seedTime); tctx.drawImage(video, 0, 0, W, H);
        var seedTmpl = grayPatch(tctx, W, H, seedBX, seedBY, seedBW, seedBH);
        var seedSig = frameSig(tctx, W, H);
        var seedKf = { time: +seedTime.toFixed(3), x: seedBX / W, y: seedBY / H, w: seedBW / W, h: seedBH / H };

        // One directional walk from the seed frame; dir=+1 forward, -1 backward.
        // Each direction re-seeds a fresh template copy so drift can't cross the seed.
        async function walk(dir) {
            var tmpl = { g: seedTmpl.g.slice(), w: seedTmpl.w, h: seedTmpl.h, mean: seedTmpl.mean, std: seedTmpl.std };
            var bx = seedBX, by = seedBY, bw = seedBW, bh = seedBH;
            var prevSig = seedSig, motionBase = [], lowStreak = 0, out = [];
            var span = dir > 0 ? (endT - seedTime) : (seedTime - startT);
            var steps = Math.max(0, Math.ceil(span / interval));
            for (var i = 1; i <= steps; i++) {
                if (opts.signal && opts.signal.aborted) { lostReason = 'aborted'; break; }
                var t = dir > 0 ? Math.min(seedTime + i * interval, endT) : Math.max(seedTime - i * interval, startT);
                await seek(t); tctx.drawImage(video, 0, 0, W, H);
                var sig = frameSig(tctx, W, H), gdiff = sigDiff(sig, prevSig); prevSig = sig;
                var base = median(motionBase);
                if (i > 1 && gdiff > CUT_ABS && gdiff > base * CUT_REL) { lostReason = 'cut'; break; }
                motionBase.push(gdiff); if (motionBase.length > 8) motionBase.shift();
                var res = nccSearch(tctx, W, H, tmpl, bx, by, bw, bh, searchGain);
                if (res && res.score >= NCC_LOST) {
                    lowStreak = 0; bx = clamp(res.x, 0, W - bw); by = clamp(res.y, 0, H - bh);
                    if (res.score > 0.5) blendTemplate(tmpl, grayPatch(tctx, W, H, bx, by, bw, bh), 0.2);
                } else { lowStreak++; }
                out.push({ time: +t.toFixed(3), x: bx / W, y: by / H, w: bw / W, h: bh / H });
                if (lowStreak >= NCC_LOST_MAX) { lostReason = 'lost'; break; }
                if (opts.onProgress) opts.onProgress(dir > 0 ? 0.5 + 0.5 * i / steps : 0.5 * i / steps);
            }
            return out;
        }
        var back = await walk(-1);
        var fwd = await walk(1);
        var keyframes = back.reverse().concat([seedKf], fwd);
        return { keyframes: keyframes, lost: !!lostReason, lostReason: lostReason };
    }

    // ── Build a smoothed, in-frame crop-window trajectory ───────────────────────
    // keyframes: subject boxes (normalised). cw/ch: crop window size in PIXELS.
    // Returns at(t)→{x,y} (crop top-left px, clamped) + decimated samples.
    // Smoothing = zero-phase two-pass EMA on the subject CENTRE (no lag, no jitter).
    function buildCropPath(o) {
        var kf = o.keyframes, vw = o.vw, vh = o.vh, cw = o.cw, ch = o.ch;
        // Follow softness (s). 0.25 (not 0.45) measured 2026-07-12: 0.45
        // over-smooths → the crop lags the subject → 2.5–4× worse centering
        // (fast motion maxErr 0.083 vs 0.033) for a negligible jitter reduction.
        // Fixed two-pass EMA beat a speed-adaptive variant, so it stays.
        var tau = o.tau || 0.25;
        // subject centre in px per keyframe
        var pts = kf.map(function (k) { return { t: k.time, cx: (k.x + k.w / 2) * vw, cy: (k.y + k.h / 2) * vh }; });
        if (pts.length === 1) pts.push({ t: pts[0].t + 0.001, cx: pts[0].cx, cy: pts[0].cy });
        // forward+backward EMA (zero-phase). alpha derived from dt/tau per step.
        function ema(field) {
            var v = pts.map(function (p) { return p[field]; });
            for (var i = 1; i < pts.length; i++) { var dt = Math.max(1e-3, pts[i].t - pts[i - 1].t), a = 1 - Math.exp(-dt / tau); v[i] = v[i - 1] + a * (v[i] - v[i - 1]); }
            for (var j = pts.length - 2; j >= 0; j--) { var dt2 = Math.max(1e-3, pts[j + 1].t - pts[j].t), a2 = 1 - Math.exp(-dt2 / tau); v[j] = v[j + 1] + a2 * (v[j] - v[j + 1]); }
            return v;
        }
        var sx = ema('cx'), sy = ema('cy');
        var maxX = Math.max(0, vw - cw), maxY = Math.max(0, vh - ch);
        var samples = pts.map(function (p, i) {
            return { t: p.t, x: clamp(Math.round(sx[i] - cw / 2), 0, maxX), y: clamp(Math.round(sy[i] - ch / 2), 0, maxY) };
        });
        function at(t) {
            if (t <= samples[0].t) return { x: samples[0].x, y: samples[0].y };
            var last = samples[samples.length - 1];
            if (t >= last.t) return { x: last.x, y: last.y };
            // binary search the bracketing samples
            var lo = 0, hi = samples.length - 1;
            while (hi - lo > 1) { var mid = (lo + hi) >> 1; if (samples[mid].t <= t) lo = mid; else hi = mid; }
            var a = samples[lo], b = samples[hi], r = (t - a.t) / Math.max(1e-6, b.t - a.t);
            return { x: Math.round(a.x + (b.x - a.x) * r), y: Math.round(a.y + (b.y - a.y) * r) };
        }
        return { at: at, samples: samples };
    }

    // ── FFmpeg fallback: animated crop x/y as a flat ReLU-sum expression ─────────
    // Piecewise-linear via hinge terms (no nesting → no parser-depth trap):
    //   x(t) = x0 + Σ (s_i - s_{i-1}) * max(0, t - t_i),  s_{-1}=0, final slope→0.
    // Decimate to ≤ maxPts breakpoints (smooth motion needs few).
    function ffmpegCropExpr(path, cw, ch, vw, vh, maxPts) {
        maxPts = maxPts || 48;
        var s = path.samples;
        // uniform decimation keeping first + last
        var pts = s;
        if (s.length > maxPts) {
            pts = []; var stepD = (s.length - 1) / (maxPts - 1);
            for (var i = 0; i < maxPts; i++) pts.push(s[Math.round(i * stepD)]);
            pts[pts.length - 1] = s[s.length - 1];
        }
        // Raw hinge-sum expression (FFmpeg `t` in seconds). No clamp here — the
        // caller wraps it in the crop filter's own in_w/in_h clamp (authoritative
        // on anamorphic/rotated sources, where the page's vw/vh lie).
        function build(field) {
            var terms = [String(pts[0][field])];
            var prevSlope = 0;
            for (var i = 0; i < pts.length - 1; i++) {
                var dt = Math.max(1e-3, pts[i + 1].t - pts[i].t);
                var slope = (pts[i + 1][field] - pts[i][field]) / dt;
                var d = slope - prevSlope;
                if (Math.abs(d) > 1e-6) terms.push('(' + d.toFixed(4) + ')*max(0\\,t-' + pts[i].t.toFixed(3) + ')');
                prevSlope = slope;
            }
            // cancel residual slope after the last breakpoint → hold final value
            if (Math.abs(prevSlope) > 1e-6) terms.push('(' + (-prevSlope).toFixed(4) + ')*max(0\\,t-' + pts[pts.length - 1].t.toFixed(3) + ')');
            return terms.join('+');
        }
        return { x: build('x'), y: build('y') };
    }

    root.SubjectTracker = {
        ensureDetector: ensureDetector,
        detectFaces: detectFaces,
        ensureObjectDetector: ensureObjectDetector,
        detectObjects: detectObjects,
        pickSubject: pickSubject,
        track: track,
        buildCropPath: buildCropPath,
        ffmpegCropExpr: ffmpegCropExpr,
        isDetectorReady: function () { return !!_detector; },
    };
})(typeof window !== 'undefined' ? window : globalThis);
